// ./src/components/generic/PlanFeatureNotice/PlanFeatureNotice.tsx
//
// The banner a page shows when the plan will refuse the NEXT create — "you can read and edit
// what is here; you cannot add another, and this is why."
//
// ⚠ Why this is not `PremiumFeatureGate`. That component REPLACES its children with an
// upgrade screen, which is right for a page the plan does not include at all. It is wrong
// here, and dangerously so: eventsmagic-api puts these capability gates in `beforeCreate`
// only — reads, updates and removes are untouched (checked across all 23 gated cores; the
// sole exception is EventCore, which gates two settings on update). A host whose plan lapses
// can still open, edit and export everything they already built. A full-page lock would hide
// data the server is still happily serving and block edits the server still allows, which is
// worse than showing no gate at all.
//
// So: the list stays. Only "add another" is refused, and it is refused out loud.
import React from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { isPaywallPreview } from "@/config";
import { EntitlementVerdict } from "@/utils/planEntitlement";
import { FeatureGateKind, warnOnGateMismatch } from "@/utils/planGating";

import { AppIcon } from "../AppIcon";

export interface PlanFeatureNoticeProps {
  /**
   * The verdict from `useFeatureGate().checkFeature(...)`. Renders nothing when allowed — except a PREMIUM feature a draft
   * or a test run may use (ROADMAP #231), which gets a marker: it can be tried now, and publishing will ask for it.
   */
  verdict: EntitlementVerdict | null | undefined;
  /** Send the host somewhere that can fix it — normally `useFeatureGate().goToUpgrade`. */
  onUpgrade: () => void;
  /** What the host was trying to add, for the copy: "another game", "more questions". */
  what?: string;
  /**
   * A deliberate departure from the feature's DECLARED gate kind, for the rare page that is
   * genuinely the exception.
   *
   * ⚠ It exists to be passed, not computed. A page may state "here this one is add-only"; it
   * may not derive that from a plan value — re-deriving entitlement at a call site is the bug
   * `useFeatureGate` exists to prevent, and this prop is not a back door to it.
   */
  gate?: FeatureGateKind;
  /**
   * The feature is READ-ONLY without the plan (owner, 2026-10-20 — vendor compliance): what is here can be read, but
   * nothing in it can be changed. Says so, instead of "everything already here keeps working". Stated by the page,
   * like `gate` — the server is what refuses the writes.
   */
  readOnly?: boolean;
}

/** ROADMAP #231 — a premium feature used on a draft or in test mode: allowed now, asked for at publish / go-live. */
export const isPremiumTrial = (verdict: EntitlementVerdict | null | undefined): boolean =>
  !!verdict && verdict.allowed && !!verdict.premium && (verdict.reason === "draft" || verdict.reason === "test_mode");

/**
 * ROADMAP #231 — the marker on a premium feature a draft (or a test run) is using: it works now, and publishing (going
 * live) will ask for a plan that includes it. Shown by `PlanFeatureNotice` and `PremiumFeatureGate`; renders nothing for
 * any other verdict.
 */
/**
 * #238 — the marker's words for a BACK-OFFICE tool's sample: how many of how many are left, and whether publishing asks
 * (volunteer shifts) or not (budget, vendors… — guests never see them).
 */
const sampleBody = (
  t: (key: string, options?: Record<string, unknown>) => string,
  sample: NonNullable<EntitlementVerdict["sample"]>,
  draft: boolean,
): string => {
  // The count is not known on a narrowed or unloaded list: the sentence then leaves "left" out (the server decides).
  const left = sample.count == null ? null : Math.max(0, sample.limit - sample.count);
  const where = draft ? t("common:plan.sampleDraft", { defaultValue: "A draft" }) : t("common:plan.sampleTest", { defaultValue: "A test run" });
  const base =
    left == null
      ? t("common:plan.sampleBody", { defaultValue: "{{where}} can try up to {{limit}} {{noun}}. Upgrade to add more.", where, limit: sample.limit, noun: sample.noun[1] })
      : t("common:plan.sampleLeftBody", {
          defaultValue: "{{where}} can try up to {{limit}} {{noun}} — {{left}} left. Upgrade to add more.",
          where,
          limit: sample.limit,
          noun: sample.noun[1],
          left,
        });
  const after = sample.publishAsks
    ? t("common:plan.samplePublishAsks", { defaultValue: "Publishing needs a plan that includes them — or take them off first." })
    : t("common:plan.sampleKept", { defaultValue: "Guests never see them, so publishing does not need it; they are kept." });

  return `${base} ${after}`;
};

export const PremiumTrialMarker: React.FC<{ verdict: EntitlementVerdict | null | undefined; onUpgrade: () => void }> = ({ verdict, onUpgrade }) => {
  const { t } = useTranslation(["common"]);

  if (!isPremiumTrial(verdict)) {
    return null;
  }

  return (
    <Alert
      color="yellow"
      variant="light"
      radius="md"
      icon={<AppIcon iconName="Crown" size={18} />}
      title={t("common:plan.premiumTitle", { defaultValue: "{{feature}} is a premium feature", feature: verdict!.featureName })}
    >
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Text size="md" style={{ flex: "1 1 240px" }}>
          {verdict!.sample
            ? sampleBody(t, verdict!.sample, verdict!.reason === "draft")
            : verdict!.reason === "draft"
            ? t("common:plan.premiumDraftBody", {
                defaultValue: "Use it freely while the event is a draft. Publishing needs a plan that includes it — or take it off first.",
              })
            : t("common:plan.premiumTestBody", {
                defaultValue: "Use it freely in test mode. Going live needs a plan that includes it — or take it off first.",
              })}
        </Text>

        <Button size="md" variant="light" leftSection={<AppIcon iconName="Rocket" size={16} />} onClick={onUpgrade}>
          {t("common:plan.seePlans", { defaultValue: "See plans" })}
        </Button>
      </Group>
    </Alert>
  );
};

export const PlanFeatureNotice: React.FC<PlanFeatureNoticeProps> = ({ verdict, onUpgrade, what, gate, readOnly = false }) => {
  const { t } = useTranslation(["common"]);

  if (!verdict) {
    return null;
  }

  // ROADMAP #231 — allowed only because the event is a draft / in test mode: say it is premium, and when it will be asked.
  if (isPremiumTrial(verdict)) {
    return <PremiumTrialMarker verdict={verdict} onUpgrade={onUpgrade} />;
  }

  if (verdict.allowed) {
    return null;
  }

  // ⚠ Checks ITSELF against the declaration (ROADMAP A2.10): this banner is what a feature
  // declared `gate: 'add'` is supposed to get, so rendering it for a 'page' or 'none' feature
  // is a wiring mistake. Dev-only console error; `npm run check-entitlement` catches the same
  // thing statically, before it can ship.
  warnOnGateMismatch("PlanFeatureNotice", verdict.code, gate);

  const isLimit = verdict.reason === "limit";
  // #238 — a back-office tool's sample is used up on this draft / test run: the server's own sentence says so.
  const sampleUsed = !!verdict.sample && !!verdict.message;

  const title = sampleUsed
    ? t("common:plan.sampleUsedTitle", {
        defaultValue: "{{feature}} — this draft has tried {{limit}} {{noun}}",
        feature: verdict.featureName,
        limit: verdict.sample!.limit,
        noun: verdict.sample!.noun[1],
      })
    : isLimit
    ? t("common:plan.limitTitle", {
        defaultValue: "{{feature}} — you have used all {{limit}}",
        feature: verdict.featureName,
        limit: verdict.limit ?? 0,
      })
    : t("common:plan.capabilityTitle", {
        defaultValue: "{{feature}} is not on your plan",
        feature: verdict.featureName,
      });

  const body = sampleUsed
    ? verdict.message!
    : readOnly
    ? t("common:plan.readOnlyBody", {
        defaultValue: "What is already here stays readable, but it cannot be changed. Changing {{what}} needs a plan that includes it.",
        what: what || t("common:plan.anotherOne", { defaultValue: "another" }),
      })
    : isLimit
      ? t("common:plan.limitBody", {
          defaultValue: "Everything already here keeps working — you just cannot add {{what}} until you move to a plan with more.",
          what: what || t("common:plan.anotherOne", { defaultValue: "another" }),
        })
      : t("common:plan.capabilityBody", {
          defaultValue: "Everything already here keeps working. Adding {{what}} needs a plan that includes it.",
          what: what || t("common:plan.anotherOne", { defaultValue: "another" }),
        });

  return (
    <Alert
      color={isLimit ? "yellow" : "blue"}
      variant="light"
      radius="md"
      icon={<AppIcon iconName={isLimit ? "AlertTriangle" : "Lock"} size={18} />}
      title={title}
    >
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Text size="sm" style={{ flex: "1 1 240px" }}>
          {body}
          {isPaywallPreview() ? (
            <Text component="span" size="xs" c="dimmed">
              {" "}
              {t("common:plan.previewHint", {
                defaultValue: "(preview mode — the server is not enforcing this yet)",
              })}
            </Text>
          ) : null}
        </Text>

        <Button
          size="sm"
          variant="light"
          leftSection={<AppIcon iconName="Rocket" size={16} />}
          onClick={onUpgrade}
        >
          {t("common:plan.seePlans", { defaultValue: "See plans" })}
        </Button>
      </Group>
    </Alert>
  );
};
